import { Close } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";

import { useDocumentQuery } from "AdvisoryManagement/Api/Queries/useDocumentQuery";
import { useDocumentRulesQuery } from "AdvisoryManagement/Api/Queries/useDocumentRulesQuery";

type DocumentDetailDialogProps = {
	documentId: string;
	onClose?: () => void;
};

export const DocumentDetailDialog = ({
	documentId,
	onClose,
}: DocumentDetailDialogProps) => {
	const { data: document, isLoading } = useDocumentQuery(documentId);
	const { data: rules } = useDocumentRulesQuery();

	const handleClose = () => {
		onClose?.();
	};

	if (!document && !isLoading) return null;

	const detailData = document?.documentData?.jsonData;

	return (
		<Dialog
			open={true}
			onClose={(e: React.MouseEvent<HTMLElement>) => {
				e.stopPropagation();
				handleClose();
			}}
			maxWidth="sm"
			fullWidth
		>
			{isLoading && (
				<>
					<Skeleton variant="text" height={40} />
					<Skeleton variant="text" />
					<Skeleton variant="text" />
					<Skeleton variant="text" />
					<Skeleton variant="text" height={400} />
				</>
			)}
			{document && (
				<>
					<DialogTitle>
						{document?.documentName}
						<IconButton
							aria-label="close"
							onClick={(e) => {
								e.stopPropagation();
								handleClose();
							}}
							sx={{
								position: "absolute",
								right: 16,
								top: 16,
								color: "#414651",
							}}
						>
							<Close />
						</IconButton>
					</DialogTitle>
					<DialogContent>
						<Stack direction="column" spacing={0.5} paddingY={2}>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2" color="textSecondary">
									ID dokumentu:
								</Typography>
								<Typography variant="body2" color="textPrimary">
									{document?.documentId}
								</Typography>
							</Stack>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2" color="textSecondary">
									Typ dokumentu:
								</Typography>
								<Typography variant="body2" color="textPrimary">
									{document?.documentType}
								</Typography>
							</Stack>
							<Stack direction="row" justifyContent="space-between">
								<Typography variant="body2" color="textSecondary">
									Název dokumentu:
								</Typography>
								<Typography variant="body2" color="textPrimary">
									{document?.documentName}
								</Typography>
							</Stack>
						</Stack>
						<Typography variant="body2" color="textSecondary">
							Vyžadované náležitosti pro tento dokument:
						</Typography>
						<ul>
							{rules?.IsAutoCheckRequired && <li>Automatická kontrola</li>}
							{rules?.IsCheckAgainstMeetingRecordRequired && (
								<li>Kontrola proti ZZJ</li>
							)}
							{rules?.IsSendToBORequired && <li>Odeslání na BO</li>}
							{rules?.IsSendToPartnerRequired && <li>Odeslání partnerovi</li>}
							{rules?.IsSendToClientRequired && <li>Odeslání klientovi</li>}
							{rules?.IsCLientSignatureRequired && <li>Podpis klienta</li>}
							{rules?.IsBOArchivationRequired && <li>Archivace na BO</li>}
						</ul>
						<Typography variant="body2" color="textSecondary">
							Extrahovaná data dokumentu:
						</Typography>
						<Box
							sx={{
								maxWidth: "100%",
								maxHeight: 400,
								overflow: "auto",
								backgroundColor: "#f5f5f5",
							}}
						>
							<pre>
								{JSON.stringify(JSON.parse(detailData || "{}"), null, 2)}
							</pre>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={(e) => {
								e.stopPropagation();
								handleClose();
							}}
						>
							Zavřít
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};
